import { Tooltip } from "@mui/material";
import { AlertCircle } from "lucide-react";
import moment from "moment";

import {
  COMPLETED_ORDER_STATE,
  summariseCompletedOrder,
} from "features/labOrders/utils/completedOrder";

import s from "./LabOrderWhatWasOrdered.module.css";

const formatRequestedAt = (requestedAt) => {
  const parsed = moment(requestedAt);

  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : null;
};

const TestMeta = ({ test }) => {
  const parts = [
    test.section,
    test.priority,
    !!test.requestedAt && formatRequestedAt(test.requestedAt),
  ].filter(Boolean);

  if (!parts.length) return null;

  return <p className={s.testMeta}>{parts.join(" · ")}</p>;
};

const OrderQuestion = ({ question }) => (
  <li className={s.question}>
    <span className={s.questionLabel}>{question.label}</span>
    {question.isUnreadable ? (
      <Tooltip
        title={question.reason ?? "The order did not say why."}
        placement="top"
        enterDelay={200}
      >
        <span className={s.answerUnreadable}>{question.answer}</span>
      </Tooltip>
    ) : (
      <span className={s.answer}>{question.answer}</span>
    )}
  </li>
);

const OrderedTest = ({ test }) => (
  <li className={s.test}>
    <div className={s.testHeader}>
      {/* The code is locally assigned and means nothing to a reader, but it is what a tester
          needs — so it goes on the tooltip rather than into the name. */}
      <Tooltip
        title={test.code ? `Code ${test.code}` : ""}
        placement="top"
        enterDelay={200}
      >
        <span className={s.testName}>{test.name}</span>
      </Tooltip>
      {!!test.isUnrecognisedCoding && (
        <Tooltip
          title="The sender used a coding system we have not seen before. The value is shown as sent."
          placement="top"
        >
          <span className={s.codingFlag}>unknown coding</span>
        </Tooltip>
      )}
    </div>

    <TestMeta test={test} />

    {!!test.specimen && (
      <p className={s.specimen}>
        <span className={s.fieldLabel}>Specimen</span>
        {test.specimen}
      </p>
    )}

    {!!test.questions.length && (
      <ul className={s.questions}>
        {test.questions.map((question) => (
          <OrderQuestion key={question.label} question={question} />
        ))}
      </ul>
    )}
  </li>
);

/**
 * What the GP actually ordered, read from `completedOrder` (KIN-1976).
 *
 * Until now the order was only visible through Healthlink's own stylesheet in an iframe — the
 * printed document rather than the data — so the tests could not be read, searched or laid out
 * here. The stylesheet preview stays exactly as it was; this sits above it.
 */
export const LabOrderWhatWasOrdered = ({ order }) => {
  const { state, tests, clinicalDetails } = summariseCompletedOrder(order);

  // Nothing has come back yet. The preview below already says the order appears once completed,
  // so this section stays out of the way rather than adding a second empty box.
  if (state === COMPLETED_ORDER_STATE.notDownloaded) return null;

  if (state === COMPLETED_ORDER_STATE.unreadable) {
    return (
      <section className={s.wrapper}>
        <h3 className={s.title}>What was ordered</h3>
        <div className={s.notice} role="status">
          <AlertCircle size={20} className={s.noticeIcon} aria-hidden />
          <p className={s.noticeMessage}>
            Couldn&apos;t read the completed order — see the message below.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={s.wrapper}>
      <h3 className={s.title}>What was ordered</h3>

      {!!clinicalDetails && (
        <p className={s.clinicalDetails}>
          <span className={s.fieldLabel}>Clinical details</span>
          {clinicalDetails}
        </p>
      )}

      {tests.length ? (
        <ul className={s.tests}>
          {tests.map((test) => (
            <OrderedTest key={test.key} test={test} />
          ))}
        </ul>
      ) : (
        <p className={s.noticeMessage}>
          The completed order carried no tests. The message below shows it as Healthlink sent it.
        </p>
      )}
    </section>
  );
};
